import { useCallback, useMemo } from 'react'
import { Currency, TradeV1, TradeV2, TradeType } from '@pulsex/sdk'
import { InjectedModalProps } from '@pulsex/uikit'
import { useTranslation } from '@pulsex/localization'
import TransactionConfirmationModal, {
  ConfirmationModalContent,
  TransactionErrorContent,
} from 'components/TransactionConfirmationModal'
import SwapModalFooter from './SwapModalFooter'
import SwapModalHeader from './SwapModalHeader'

/**
 * Returns true if the trade requires a confirmation of details before we can submit it
 * @param tradeA trade A
 * @param tradeB trade B
 */
function tradeMeaningfullyDiffers(
  tradeA: TradeV1<Currency, Currency, TradeType> | TradeV2<Currency, Currency, TradeType>,
  tradeB: TradeV1<Currency, Currency, TradeType> | TradeV2<Currency, Currency, TradeType>,
): boolean {
  return (
    tradeA.tradeType !== tradeB.tradeType ||
    !tradeA.inputAmount.currency.equals(tradeB.inputAmount.currency) ||
    !tradeA.inputAmount.equalTo(tradeB.inputAmount) ||
    !tradeA.outputAmount.currency.equals(tradeB.outputAmount.currency) ||
    !tradeA.outputAmount.equalTo(tradeB.outputAmount)
  )
}
interface ConfirmSwapModalProps {
  trade?: TradeV1<Currency, Currency, TradeType> | TradeV2<Currency, Currency, TradeType>
  originalTrade?: TradeV1<Currency, Currency, TradeType> | TradeV2<Currency, Currency, TradeType>
  attemptingTxn: boolean
  txHash?: string
  recipient: string | null
  allowedSlippage: number
  isMobile?: boolean
  swapErrorMessage?: string
  onAcceptChanges: () => void
  onConfirm: () => void
  customOnDismiss?: () => void
}

const ConfirmSwapModal: React.FC<InjectedModalProps & ConfirmSwapModalProps> = ({
  trade,
  originalTrade,
  attemptingTxn,
  txHash,
  recipient,
  allowedSlippage,
  isMobile,
  swapErrorMessage,
  onAcceptChanges,
  onConfirm,
  customOnDismiss,
  onDismiss,
}) => {
  const showAcceptChanges = useMemo(
    () => Boolean(trade && originalTrade && tradeMeaningfullyDiffers(trade, originalTrade)),
    [originalTrade, trade],
  )

  const { t } = useTranslation()

  const modalHeader = useCallback(() => {
    return trade ? (
      <SwapModalHeader
        trade={trade}
        showAcceptChanges={showAcceptChanges}
      />
    ) : null
  }, [showAcceptChanges, trade])

  const modalBottom = useCallback(() => {
    return trade ? (
      <SwapModalFooter
        onConfirm={onConfirm}
        trade={trade}
        recipient={recipient}
        disabledConfirm={showAcceptChanges}
        swapErrorMessage={swapErrorMessage}
        allowedSlippage={allowedSlippage}
        showAcceptChanges={showAcceptChanges}
        isMobile={isMobile}
        onAcceptChanges={onAcceptChanges}
      />
    ) : null
  }, 
  [
    allowedSlippage, 
    onConfirm, 
    showAcceptChanges, 
    swapErrorMessage, 
    trade, 
    isMobile, 
    onAcceptChanges, 
    recipient
  ])

  // text to show while loading
  const pendingText = t('Swapping %amountA% %symbolA% for %amountB% %symbolB%', {
    amountA: trade?.inputAmount?.toSignificant(6) ?? '',
    symbolA: trade?.inputAmount?.currency?.symbol ?? '',
    amountB: trade?.outputAmount?.toSignificant(6) ?? '',
    symbolB: trade?.outputAmount?.currency?.symbol ?? '',
  })

  const confirmationContent = useCallback(
    () =>
      swapErrorMessage ? (
        <TransactionErrorContent onDismiss={onDismiss} message={swapErrorMessage} />
      ) : (
        <ConfirmationModalContent topContent={modalHeader} bottomContent={modalBottom} />
      ),
    [onDismiss, modalBottom, modalHeader, swapErrorMessage],
  )

  return (
    <TransactionConfirmationModal
      title={t('Confirm Swap')}
      onDismiss={onDismiss}
      customOnDismiss={customOnDismiss}
      attemptingTxn={attemptingTxn}
      hash={txHash}
      content={confirmationContent}
      pendingText={pendingText}
      currencyToAdd={trade?.outputAmount.currency}
    />
  )
}

export default ConfirmSwapModal
